import type { ImageAttachment } from '../types/report';

/**
 * Convert a File object to a base64 data URL
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Get image dimensions from a base64 data URL
 */
export function getImageDimensions(base64: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = base64;
  });
}

/**
 * Calculate proportional image dimensions for PDF rendering
 * @param originalWidth Original image width in pixels
 * @param originalHeight Original image height in pixels
 * @param maxWidth Maximum width in PDF units
 * @param maxHeight Maximum height in PDF units
 */
export function calculatePDFImageSize(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  const aspectRatio = originalWidth / originalHeight;

  let width = maxWidth;
  let height = maxWidth / aspectRatio;

  // If calculated height exceeds max, recalculate based on height
  if (height > maxHeight) {
    height = maxHeight;
    width = maxHeight * aspectRatio;
  }

  return { width, height };
}

/**
 * Process a file and create an ImageAttachment object
 */
export async function processImageFile(file: File): Promise<ImageAttachment> {
  const base64 = await fileToBase64(file);
  const dimensions = await getImageDimensions(base64);

  const format: 'JPEG' | 'PNG' = file.type.includes('png') ? 'PNG' : 'JPEG';

  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    base64,
    filename: file.name,
    format,
    width: dimensions.width,
    height: dimensions.height,
    size: file.size,
  };
}

/**
 * Validate if a file is a supported image type
 */
export function isValidImageFile(file: File): boolean {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  return validTypes.includes(file.type.toLowerCase());
}

/**
 * Convert an image URL to base64 data URL
 */
export async function urlToBase64(url: string): Promise<string> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert URL to base64:', error);
    throw error;
  }
}
